/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useState } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import { LoaderBar, Page } from 'src/components';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import ListIcon from '@material-ui/icons/List';
import {
  Box,
  Card,
  Table,
  makeStyles,
  colors,
  Container,
  Button,
  Typography,
  Grid
} from '@material-ui/core';
import { SalesColumnsFilterModal } from 'src/components/modal/sales';
import { usePermissions, useSnackBar } from 'src/hooks';
import NotFoundView from 'src/views/errors/NotFoundView';
import SalesToolbar from './components/SalesToolbar';
import Toolbar from './components/Toolbar';
import { SalesTableHeader } from './components';
import { SalesTableBody } from './components/SalesTableBody';
import { Alert } from '@material-ui/lab';
import { PaginationMeta } from 'src/types';
import {
  downloadCSV,
  formatCurrency,
  getDateRangePreset,
  salesAmountsToString,
  salesItemToString,
  salesPaymentToString
} from 'src/utils';
import { cloneDeep } from 'lodash';
import { SalesCardListings } from './components/SalesListCard';
import PaymentIcon from '@material-ui/icons/Payment';
import { TransactionsWithBalanceModal } from 'src/components/modal/sales/TransactionsWithBalanceModal';
import { unwrapResult } from '@reduxjs/toolkit';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Fade } from '@material-ui/core';
import { cleanDate } from 'src/utils/dateTimeUtils';

const { actions: salesActions, selectors: salesSelectors } = slices.sales;

const salesBasicItemToString = (arr?: any[]) =>
  arr?.reduce((string, item) => {
    const itemDetails = `${item?.product_name}\n${item?.serial_no}\nQTY: ${
      item?.quantity
    } | RP: ${formatCurrency(
      item?.retail_price || 0
    )}\n___________________________________`;
    return string.concat(`${itemDetails}\r\n`);
  }, '');

const salesBasicAmountsToString = (amount?: {
  total_retail_price?: number;
  amount_paid?: number;
  balance?: number;
}) => `
  Total Retail Price: ${formatCurrency(amount?.total_retail_price)}
  Amount Paid: ${formatCurrency(amount?.amount_paid)}
  Balance: ${formatCurrency(amount?.balance)}
  `;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  salesCard: {
    height: '100%'
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  tableRow: {
    height: 35,
    padding: 10
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  retailPriceCell: {
    width: 200
  },
  columnBtn: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    display: 'flex'
  },
  toCsvText: {
    color: theme.palette.primary.main
  },
  toCsvBtn: {
    marginLeft: theme.spacing(2)
  },
  displayColumn: {
    [theme.breakpoints.up('md')]: {
      display: 'flex'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  displaySalesMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'block'
    },
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  }
}));

const SalesList = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const permissions = usePermissions();
  const { canExtractSalesPayments } = permissions;
  const isBasicSalesDashboard =
    !permissions?.canViewSales && permissions?.canViewSalesBasicDashboard;
  const canViewSalesPage =
    permissions?.canViewSales || permissions?.canViewSalesBasicDashboard;
  const sales = useAppSelector(salesSelectors.selectAllSales);
  const salesColumns = useAppSelector(salesSelectors.selectSalesColumns);
  const salesFilter = useAppSelector(salesSelectors.selectAllSalesFilter);
  const salesComputation = useAppSelector(
    salesSelectors.selectSalesResponseComputation
  );

  const { preset_dates } = salesFilter;

  const preset: { start: string; end: string } | null = preset_dates
    ? getDateRangePreset(preset_dates, false)
    : null;

  const [increasedSales, setIncreasedSales] = useState<string>('0');

  const salesDataForCsv = useMemo(() => {
    const clonedSales = cloneDeep(sales);
    const salesData = clonedSales?.map((sale) => ({
      ...sale,
      items: isBasicSalesDashboard
        ? salesBasicItemToString(sale?.items)
        : salesItemToString(sale?.items)?.toString(),
      payments: salesPaymentToString(sale?.payments)
    }));

    const salesResult = salesData
      .map((sales) => {
        return {
          date_sold: salesColumns?.date_sold ? sales?.date_sold : undefined,
          date_purchased: salesColumns?.date_purchased
            ? sales?.date_purchased
            : undefined,
          branch: salesColumns?.branch ? sales?.branch : undefined,
          customer_name: salesColumns?.customer_name
            ? sales?.customer_name
            : undefined,
          order_no: salesColumns?.order_no ? sales?.order_no : undefined,
          transaction_no: salesColumns?.transaction_no
            ? sales?.transaction_no
            : undefined,
          warranty_slip_no: salesColumns?.warranty_slip_no
            ? sales?.warranty_slip_no
            : undefined,
          acknowledgement_receipt_no: salesColumns?.acknowledgement_receipt_no
            ? sales?.acknowledgement_receipt_no
            : undefined,
          items: salesColumns?.products ? sales?.items : undefined,
          payments: salesColumns?.payments ? sales?.payments : undefined,
          amount: salesColumns?.amounts
            ? isBasicSalesDashboard
              ? salesBasicAmountsToString({
                  total_retail_price: sales?.total_retail_price,
                  amount_paid: sales?.amount_paid,
                  balance: sales?.balance
                })
              : salesAmountsToString({
                  total_retail_price: sales?.total_retail_price,
                  total_dealer_price: sales?.total_dealers_price,
                  amount_paid: sales?.amount_paid,
                  balance: sales?.balance,
                  gross: sales?.total_gross
                })
            : undefined
        };
      })
      .map((item) => {
        const filteredItem = Object.fromEntries(
          // eslint-disable-next-line no-unused-vars
          Object.entries(item).filter(([_, value]) => value !== undefined)
        );

        return filteredItem;
      });

    return salesResult;
  }, [
    sales,
    salesColumns.acknowledgement_receipt_no,
    salesColumns.amounts,
    salesColumns.branch,
    salesColumns.customer_name,
    salesColumns.date_purchased,
    salesColumns.date_sold,
    salesColumns.order_no,
    salesColumns.payments,
    salesColumns.products,
    salesColumns.transaction_no,
    salesColumns.warranty_slip_no,
    isBasicSalesDashboard
  ]);

  const isNextPageLoading = useAppSelector(
    salesSelectors.selectSalesLoadingNextPage
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchListLoading, setIsFetchListLoading] = useState<boolean>(false);
  const [isComputeLoading, setIsComputeLoading] = useState<boolean>(false);
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [balanceModalOpen, setBalanceModalOpen] = useState<boolean>(false);

  const [page, setPage] = useState<number | null>(null);
  const [lastPage, setLastPage] = useState<number | null>(null);

  const getSalesNextPages = useCallback(async () => {
    const resNextPage: any = await dispatch(
      salesActions.getSalesNextPageThunk()
    );
    const resNxtPgeMeta: PaginationMeta =
      resNextPage?.payload?.originalData?.meta;

    if (!resNxtPgeMeta) return;

    const currentPage = resNxtPgeMeta.current_page || 1;
    const lastPage = resNxtPgeMeta.last_page || 1;

    setPage(currentPage);
    setLastPage(lastPage);

    if (currentPage <= lastPage) {
      await getSalesNextPages();
    }
  }, [dispatch]);

  const getSalesV2 = useCallback(
    async (filter = salesFilter) => {
      setIsComputeLoading(true);
      const { preset_dates } = filter;

      setIncreasedSales('0'); // reset sales
      const currentData = unwrapResult(
        await dispatch(salesActions.getSalesComputationThunk())
      );

      if (currentData.success && preset_dates === null) {
        const lastMonthData = unwrapResult(
          await dispatch(salesActions.getSalesComputationThunkLastMonth())
        );
        if (lastMonthData.success) {
          const oldRetailPrice =
            lastMonthData.originalData.total_retail_price || 0;
          const currentRetailPrice =
            currentData.originalData.total_retail_price || 0;

          const growthPercent =
            oldRetailPrice !== 0
              ? ((currentRetailPrice - oldRetailPrice) / oldRetailPrice) * 100
              : 0;
          setIncreasedSales(growthPercent.toFixed(3));
        } else {
          snackbar.show({
            severity: 'error',
            message:
              'Failed to compute sales growth percentage, please try again or contact support'
          });
          console.error('Failed getSalesComputationThunkLastMonth');
        }
        setIsComputeLoading(false);
      }

      if (preset_dates) {
        const test = getDateRangePreset(preset_dates, true);
        const previousSalesData = unwrapResult(
          await dispatch(
            salesActions.getSalesComputationThunkLastMonth({
              sold_date_from: test?.start,
              sold_date_to: test?.end,
              is_preset: true // meaning we calculate and fetch data by preset
            })
          )
        );

        if (previousSalesData?.success) {
          const oldRetailPrice =
            previousSalesData?.originalData?.total_retail_price || 0;
          const currentRetailPrice =
            currentData.originalData.total_retail_price || 0;

          const growthPercent =
            oldRetailPrice !== 0
              ? ((currentRetailPrice - oldRetailPrice) / oldRetailPrice) * 100
              : 0;
          setIncreasedSales(growthPercent.toFixed(3));
        }
      }

      setIsComputeLoading(false);
      //if there is new filter request empty sales list
      dispatch(salesActions.resetSalesDataOnApplyFilter());
      setShowList(true);
    },
    [dispatch, preset_dates, snackbar]
  );

  const getSalesList = useCallback(async () => {
    setIsLoading(true);
    setIsFetchListLoading(true);
    setShowList(true);

    try {
      const res: any = await dispatch(salesActions.getSalesThunk());
      const firstPageMeta: PaginationMeta = res?.payload?.originalData?.meta;

      if (firstPageMeta) {
        setPage(firstPageMeta.current_page || 1);
        setLastPage(firstPageMeta.last_page || 1);

        if (
          (firstPageMeta.current_page ?? 1) < (firstPageMeta.last_page ?? 1)
        ) {
          await getSalesNextPages();
        }
      }
    } finally {
      setIsLoading(false);
      setIsFetchListLoading(false);
      setPage(null);
      setLastPage(null);
    }
  }, [dispatch, getSalesNextPages]);

  const onShowList = useCallback(() => {
    getSalesList();
  }, [getSalesList]);

  const getSalesPayments = useCallback(async () => {
    const res: any = await dispatch(
      salesActions.getSalesPaymentThunk({
        sold_date_from: preset?.start || salesFilter.sold_date_from,
        sold_date_to: preset?.end || salesFilter.sold_date_to,
        payment_type: salesFilter.payment_type,
        branch_ids: salesFilter.branch_ids
      })
    );
    return res?.payload?.originalData?.data ?? [];
  }, [dispatch, preset, salesFilter]);

  const onClickGetSalesPayment = async () => {
    const salesPayments = await getSalesPayments();

    const fromDate = cleanDate(
      salesFilter.sold_date_from ?? preset?.start ?? ''
    );
    const toDate = cleanDate(salesFilter.sold_date_to ?? preset?.end ?? '');

    const dateLabel =
      fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`;

    downloadCSV(salesPayments, `Customer Payments - ${dateLabel}`);
  };

  return canViewSalesPage ? (
    <Page className={classes.root} title="Sales">
      <Container maxWidth={false}>
        <Toolbar
          onApplyFilterPress={getSalesV2}
          isBasicDashboard={isBasicSalesDashboard}
        />

        {sales ? (
          <SalesToolbar
            isLoading={isComputeLoading}
            increasedSalesVal={increasedSales}
            isBasicDashboard={isBasicSalesDashboard}
          />
        ) : null}

        <Grid
          container
          spacing={2}
          style={{ marginTop: '1rem', marginBottom: '1rem' }}
        >
          {showList && (
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                endIcon={
                  <>
                    <ListIcon />
                    {isFetchListLoading && (
                      <Fade in={isFetchListLoading || isLoading}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', ml: 1 }}
                          style={{
                            opacity: isFetchListLoading || isLoading ? 1 : 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <CircularProgress
                            size={20}
                            style={{ marginRight: 4 }}
                          />
                          <Typography variant="body2">
                            {lastPage ? `${page}/${lastPage}` : '...'}{' '}
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                  </>
                }
                onClick={onShowList}
                color="primary"
                fullWidth
              >
                Show List
              </Button>
            </Grid>
          )}

          {!isBasicSalesDashboard &&
          canExtractSalesPayments &&
          salesComputation.total_gross &&
          ((salesFilter.sold_date_from && salesFilter.sold_date_to) ||
            salesFilter.preset_dates) ? (
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                endIcon={
                  <>
                    <GetAppIcon />
                    {isFetchListLoading && (
                      <Fade in={isFetchListLoading || isLoading}>
                        <CircularProgress size={20} style={{ marginLeft: 5 }} />
                      </Fade>
                    )}
                  </>
                }
                color="primary"
                onClick={onClickGetSalesPayment}
                fullWidth
                disabled={isFetchListLoading}
              >
                Extract Payments
              </Button>
            </Grid>
          ) : null}

          {!isBasicSalesDashboard ? (
            <Grid
              item
              xs={12}
              md={3}
              className={classes.displayColumn}
              style={{
                justifyContent: 'flex-end'
              }}
            >
              <Button
                variant="outlined"
                endIcon={<ViewWeekIcon />}
                onClick={() => setFilterVisible(!filterVisible)}
                color="primary"
                fullWidth
              >
                Columns
              </Button>
            </Grid>
          ) : null}
          <Grid
            item
            xs={12}
            md={3}
            style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            {sales?.length ? (
              <>
                <Button
                  variant="outlined"
                  endIcon={
                    <>
                      <GetAppIcon />
                      {isFetchListLoading && (
                        <Fade in={isFetchListLoading || isLoading}>
                          <CircularProgress
                            size={20}
                            style={{ marginLeft: 5 }}
                          />
                        </Fade>
                      )}
                    </>
                  }
                  color="primary"
                  onClick={() => downloadCSV(salesDataForCsv, 'sales')}
                  fullWidth
                  disabled={isFetchListLoading}
                >
                  Export to CSV
                </Button>
              </>
            ) : null}
          </Grid>
          {!isBasicSalesDashboard &&
            sales?.some((item) => (item?.balance || 0) !== 0) && (
              <Grid
                item
                xs={12}
                md={3}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <>
                  <Button
                    variant="outlined"
                    endIcon={
                      <>
                        <PaymentIcon />
                        {isFetchListLoading && (
                          <Fade in={isFetchListLoading || isLoading}>
                            <CircularProgress
                              size={20}
                              style={{ marginLeft: 5 }}
                            />
                          </Fade>
                        )}
                      </>
                    }
                    color="primary"
                    onClick={() => setBalanceModalOpen(true)}
                    fullWidth
                    disabled={isFetchListLoading}
                  >
                    Transaction Balances
                  </Button>
                </>
              </Grid>
            )}
        </Grid>

        {!isBasicSalesDashboard ? (
          <Alert severity="info">
            <Typography variant="subtitle2">
              DP - Dealers Price | TDP - Total Dealers Price | RP - Retail Price
              | TRP - Total Retail Price
            </Typography>
            <Typography variant="subtitle2">
              Amount Paid - Total of payments | Balance - Need payments
            </Typography>
            <Typography variant="subtitle2">Gross = TRP - TDP</Typography>
          </Alert>
        ) : null}
        <Box mt={2}>
          <LoaderBar isLoading={isLoading} />
          <Card>
            <PerfectScrollbar>
              <Box className={classes.displaySalesMobile}>
                <SalesCardListings hideSensitive={isBasicSalesDashboard} />
              </Box>
              {/*if column is not visible, make the table hide*/}
              <Box minWidth={1050} className={classes?.displayColumn}>
                <Table size="small">
                  <SalesTableHeader onColumnFilterClick={() => {}} />
                  <SalesTableBody hideSensitive={isBasicSalesDashboard} />
                </Table>
                <LoaderBar isLoading={isNextPageLoading} />
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
      </Container>
      <SalesColumnsFilterModal
        visible={filterVisible}
        onHandleClose={() => setFilterVisible(false)}
      />
      <TransactionsWithBalanceModal
        open={balanceModalOpen}
        handleClose={() => setBalanceModalOpen(false)}
      />
    </Page>
  ) : (
    <NotFoundView />
  );
};

export default SalesList;
